/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  EmailAttachment,
  newTaxRate,
  PbComponentItemFinderListItem,
  PrinterFinderListItem,
  TaxRate,
} from '@gh/builder-shared';
import {
  ContactDeliveryMethod,
  JobChangeOrderConfirmationItemDto,
  JobChangeOrderDetailsDto,
  JobChangeOrderDetailsElementDto,
  JobChangeOrderDetailsMainDto,
  JobChangeOrderDetailsSaveDto,
  JobChangeOrderDetailsSaveResponseDto,
  JobChangeOrderDrawStageDto,
  JobChangeOrderElementIdsGetResponseDto,
  JobChangeOrderGeneratedDocumentSetDto,
  JobChangeOrderProduceRequestDto,
  JobChangeOrderProduceResponseDto,
  JobChangeOrderReplaceOrdersRequestDto,
  JobVariationStatus,
  LetterDto,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  EMPTY_FORM_STATE_SNAPSHOT,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  NumberTypes,
  setEntityField,
  setUpdatedDirtyType,
  TextTypes,
} from '@gh/common-shared';
import {
  AutocompleteItem,
  DirtyType,
  EMPTY_ID,
  EMPTY_PRODUCE_STATUS,
  ID,
  isProduceStatusInProgress,
  ProduceStatusDto,
  WithDirtyType,
  WithEntityState,
  WithRecalculationNeeded,
  WithVersion,
} from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { cast, currentDate, isNotNil, normalizeDate } from '@gh/core-util';
import { find, get, isEmpty, isNil, omit, omitBy, pick, sortBy, values } from 'lodash';

export enum JobCoElementType { Normal, Total }

export type JobCoElement = WithDirtyType<WithEntityState<JobChangeOrderDetailsElementDto>> & {
  variationId: ID;
  type: JobCoElementType.Normal;
  isExistInJob: boolean;
};
export type JobCoElementTotal = {
  variationId: ID;
  credit?: number;
  debit?: number;
  priceWithGst?: number;
  variationTotalPriceWithGST?: number;
  type: JobCoElementType.Total;
};
export type JobCoElementOrTotal = JobCoElement | JobCoElementTotal;

export type JobChangeOrderElementIdsGetResponse = JobChangeOrderElementIdsGetResponseDto;
export type JobCoDetailsDrawStage = JobChangeOrderDrawStageDto;
export type JobCoDetailsMasterInfo = {
  description: string;
  timeExtensionDays: number;
  priceBook?: AutocompleteItem;
  memo: string;
  isRounded: boolean;
  drawRuleName: string;
  drawRuleStage?: number;
  status: JobVariationStatus;
  isStatusChanged: boolean;
  producedDate?: Date;
  invoiceDate: Date;
  isClaimed: boolean;
  isLendersSchedule: boolean;
};

export type JobCoDetailsBottomInfo = {
  taxRate?: TaxRate;
  gstApplies: boolean;
};

export type JobCoConfirmationItem = JobChangeOrderConfirmationItemDto;

export type JobCoDetails = WithRecalculationNeeded<WithVersion<{
  variationId: ID;
  variationNumber: number;
  jobId: ID;
  jobNumber: string;
  customerId: ID;
  customerName: string;
  customerEmail: string;
  siteAddress: string;
  addedDate: Date;
  elements: JobCoElement[];
  drawRuleStages: JobCoDetailsDrawStage[];
  isFinalized: boolean;
  isNotProceeding: boolean;
  isPrintOnProcess: boolean;
  masterInfo: FormStateSnapshot<JobCoDetailsMasterInfo>;
  bottomInfo: FormStateSnapshot<JobCoDetailsBottomInfo>;
  derivedInfo: JobCoDetailsDerivedInfo;
  focusedCell?: FocusedCell;
  produceStatus: JobCoDetailsProduceStatus;
  ordersCount: number;
  ordersCountPrint: number;
  priceWithGst?: number;
  confirmation: JobCoConfirmationItem[];
  deliveryBy: ContactDeliveryMethod;
}>>;

export type JobCoSave = JobCoDetails & {
  letterTemplate?: JobCoLetter;
  quoteFilter?: JobCoDetailsQuoteFilter;
  affectedCostCentersCount?: number;
};

export type JobCoDetailsDerivedInfo = {
  amount: number;
  gstAmount: number;
  amountWithGst: number;
  variationTotalPriceWithGST?: number;
};

export type JobCoDetailsQuoteFilter = {
  price: boolean;
  quantity: boolean;
  units: boolean;
};

export type JobCoLetter = LetterDto;

export type JobCoDetailsProduceStatus = ProduceStatusDto;
export type JobCoDetailsGeneratedDocumentSet = JobChangeOrderGeneratedDocumentSetDto;
export type JobCoDetailsPrintRequest = {
  printerId?: ID;
  authCode?: string;
  docs: JobCoDetailsGeneratedDocumentSet;
};

export type JobCoProduceRequest = JobChangeOrderProduceRequestDto;
export type JobCoReplaceOrdersRequest = JobChangeOrderReplaceOrdersRequestDto;
export type JobCoProduceResponse = JobChangeOrderProduceResponseDto;

export enum JobCoUsePrinter { Mandatory, Optional, NotRequired }

export enum JobCoDocumentType { Quotation, ChangeOrderConfirmation, Invoice, ProgressDrawStatement, Orders }

export const JOB_CO_EMPTY_PRODUCE_STATUS: JobCoDetailsProduceStatus = { processed: 0, total: 0 };

export const JOB_CO_DETAILS_INIT_STATE: JobCoDetails = {
  variationId: EMPTY_ID,
  variationNumber: 0,
  jobId: EMPTY_ID,
  jobNumber: '',
  customerId: EMPTY_ID,
  customerName: '',
  customerEmail: '',
  siteAddress: '',
  addedDate: currentDate(),
  drawRuleStages: [],
  isFinalized: false,
  isNotProceeding: false,
  masterInfo: EMPTY_FORM_STATE_SNAPSHOT,
  bottomInfo: EMPTY_FORM_STATE_SNAPSHOT,
  elements: [],
  isPrintOnProcess: false,
  derivedInfo: <any>{
    amount: 0,
    gstAmount: 0,
  },
  produceStatus: EMPTY_PRODUCE_STATUS,
  ordersCount: 0,
  ordersCountPrint: 0,
  confirmation: [],
  deliveryBy: ContactDeliveryMethod.Mail,
};

const JOB_CO_MASTER_INFO_ENTITY_CONFIG: EntityConfig<JobCoDetailsMasterInfo> = {
  description: { required: true, type: TextTypes.ChangeOrderName },
  timeExtensionDays: { type: NumberTypes.Days },
  priceBook: { required: true },
  drawRuleStage: { required: ({ isLendersSchedule }) => !isLendersSchedule },
  invoiceDate: { required: true },
};

const JOB_CO_BOTTOM_INFO_ENTITY_CONFIG: EntityConfig<JobCoDetailsBottomInfo> = {
  taxRate: { required: ({ gstApplies }) => gstApplies },
};

const JOB_CO_ELEMENT_ENTITY_CONFIG: EntityConfig<JobCoElement> = {
  description: {
    editable: isJobCoElement, required: true, type: TextTypes.Text,
  },
  quantity: {
    editable: isJobCoElement,
    required: true,
    type: NumberTypes.SignedQuantity,
  },
};

const generateJobCoVariationId = (variationItemId: ID, isComponent: boolean) =>
  isComponent ? `component:${variationItemId}` : `item:${variationItemId}`;

export function isJobCoElementTotal(element: JobCoElementOrTotal): element is JobCoElementTotal {
  return element.type === JobCoElementType.Total;
}

export function isJobCoElement(element: JobCoElementOrTotal): element is JobCoElement {
  return element.type === JobCoElementType.Normal;
}

export const isJobCoElementNotEnoughQuantityForMove = ({ isCredit, quantity, origQuantity }: JobCoElement): boolean => {
  const isMoveToCredit = !isCredit;
  if (isMoveToCredit && quantity >= 0 || !isMoveToCredit && quantity <= 0) {
    const removedQuantity = (isMoveToCredit ? 1 : -1) * quantity;
    return origQuantity <= 0 || removedQuantity > origQuantity;
  } else {
    return false;
  }
};

export const jobCoElementsFromFinder = ({ componentIds, itemIds }: JobChangeOrderElementIdsGetResponse,
                                        elements: PbComponentItemFinderListItem[]): JobCoElement[] => {
  const components = elements.filter((el) => el.isComponent)
    .map((el, i) => jobCoElementFromFinderListItem(componentIds[i], el));
  const items = elements.filter((el) => !el.isComponent)
    .map((el, i) => jobCoElementFromFinderListItem(itemIds[i], el));
  return components.concat(items);
};

export const jobCoElementFromFinderListItem = (variationItemId: ID,
                                               item: PbComponentItemFinderListItem): JobCoElement => ({
  variationId: generateJobCoVariationId(variationItemId, item.isComponent),
  isComponent: item.isComponent,
  costCenterId: item.costCenterId,
  costCenterCode: Number(item.costCenterCode),
  description: item.name,
  isCredit: item.price < 0,
  isInPriceBook: true,
  code: item.code,
  itemId: item.itemId,
  price: item.price,
  priceExtension: item.price,
  deletePct: item.deletePct,
  memo: item.memo,
  quantity: 1,
  orderQuantity: 1,
  origQuantity: item.origQuantity,
  uomCode: item.uomCode,
  priceWithGst: item.priceWithGst,
  variationItemId,
  dirtyType: DirtyType.New,
  type: JobCoElementType.Normal,
  isExistInJob: isNotNil(item.origQuantity),
});

export const jobCoElementTotal = (elements: JobCoElement[]): JobCoElementTotal => {

  const debitElements = elements.filter((element) => !element.isCredit);
  const debitTotal = debitElements.reduce((sum, element) => {
    return sum + element.priceExtension;
  }, 0);
  const creditElements = elements.filter((element) => element.isCredit);
  const creditTotal = creditElements.reduce((sum, element) => {
    return sum + element.priceExtension;
  }, 0);

  const priceWithGstTotal = elements.reduce((sum, element) => {
    const value = element.priceWithGst ?? 0;
    const adjustedValue = element.isCredit ? -value : value;
    const newSum = sum + adjustedValue;  
    return newSum;
  }, 0);
  return {
    variationId: 'variation:footer',
    type: JobCoElementType.Total,
    debit: debitTotal,
    credit: creditTotal,
    variationTotalPriceWithGST: priceWithGstTotal,
  };
};


export const getJobCoElementId = (element: JobCoElement) => element.variationId;
export const validateJobCoElementField = fieldValidatorByEntityConfig(JOB_CO_ELEMENT_ENTITY_CONFIG);
export const jobCoElementFieldState = fieldStateByEntityConfig(JOB_CO_ELEMENT_ENTITY_CONFIG);
export const setJobCoElementField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validateJobCoElementField));

export const validateJobCoMasterInfo = entityValidatorByEntityConfig(JOB_CO_MASTER_INFO_ENTITY_CONFIG);
export const validateJobCoBottomInfo = entityValidatorByEntityConfig(JOB_CO_BOTTOM_INFO_ENTITY_CONFIG);

export const jobCoElementArr = createArrayHelper(getJobCoElementId);

const JOB_CO_DETAILS_MAIN_COPIED_PROPERTIES = [
  'jobId', 'variationId', 'variationNumber', 'jobNumber', 'customerId', 'customerName', 'customerEmail',
  'siteAddress', 'addedDate', 'isFinalized', 'isNotProceeding',
  'ordersCount', 'ordersCountPrint', 'version', 'confirmation', 'deliveryBy',
];

export const jobCoDetailsFromDto = ({ changeOrder: mainDto, drawStages }: JobChangeOrderDetailsDto): JobCoDetails => ({
  ...setEntityField(jobCoDetailsFromMainDto(mainDto), 'isPrintOnProcess', true),
  drawRuleStages: sortBy(drawStages, 'stageNumber'),
});

export const jobCoDetailsFromMainDto = (dto: JobChangeOrderDetailsMainDto): JobCoDetails => ({
  ...<any>pick(dto, JOB_CO_DETAILS_MAIN_COPIED_PROPERTIES),
  masterInfo: formStateSnapshot({
    description: dto.description,
    timeExtensionDays: dto.timeExtensionDays,
    priceBook: dto.priceBookId && dto.priceBookName
      ? { id: dto.priceBookId, name: dto.priceBookName }
      : void 0,
    memo: dto.memo,
    drawRuleId: dto.drawRuleId,
    drawRuleName: dto.drawRuleName,
    drawRuleStage: dto.drawRuleStage,
    isRounded: dto.isRounded,
    status: dto.status,
    isStatusChanged: false,
    producedDate: dto.producedDate,
    invoiceDate: normalizeDate(currentDate()),
    isClaimed: dto.isClaimed,
    isLendersSchedule: dto.isLendersSchedule,
  }),
  bottomInfo: formStateSnapshot({
    // return tax rate only if gstApplies is set, otherwise return it as is for EDIT mode or empty for CREATE mode
    taxRate: dto.taxType && dto.taxName && (dto.isGstApplies || isNotNil(dto.variationId)) ?
      newTaxRate(dto.taxType, dto.taxName, dto.taxRate)
      : void 0,
    gstApplies: dto.isGstApplies,
  }),
  elements: dto.variationItems.map(jobCoElementFromDto),
  isPrintOnProcess: false,
  derivedInfo: {
    amount: dto.amount || 0,
    gstAmount: dto.gstAmount || 0,
    amountWithGst: dto.amountWithGst || 0,
  },
  version: dto.version,
  produceStatus: EMPTY_PRODUCE_STATUS,
});

export const jobCoElementFromDto = (dto: JobChangeOrderDetailsElementDto): JobCoElement => ({
  ...dto,
  variationId: generateJobCoVariationId(dto.variationItemId, dto.isComponent),
  type: JobCoElementType.Normal,
  isExistInJob: isNotNil(dto.origQuantity),
});

export const jobCoSaveToMainDto = (model: JobCoSave): JobChangeOrderDetailsMainDto => {
  const masterInfo = getFormStateSnapshotValue(model.masterInfo);
  const { taxRate } = getFormStateSnapshotValue(model.bottomInfo);
  return <JobChangeOrderDetailsMainDto>{
    ...cast<JobChangeOrderDetailsMainDto>(omit(masterInfo, ['priceBook', 'isStatusChanged'])),
    ...cast<JobChangeOrderDetailsMainDto>(pick(model, JOB_CO_DETAILS_MAIN_COPIED_PROPERTIES)),
    ...cast<JobChangeOrderDetailsMainDto>(model.derivedInfo),
    taxRate: taxRate ? taxRate.taxRate : void 0,
    taxType: taxRate ? taxRate.taxType : void 0,
    taxName: taxRate ? taxRate.name : void 0,
    isRounded: masterInfo.isRounded,
    priceBookId: masterInfo.priceBook && masterInfo.priceBook.id,
    variationItems: model.elements.map(jobCoElementToDto),
  };
};

export const jobCoSaveToDto = (model: JobCoSave, isSave: boolean): JobChangeOrderDetailsSaveDto => {
  const { quoteFilter } = model;
  return <JobChangeOrderDetailsSaveDto>{
    jobChangeOrder: jobCoSaveToMainDto(model),
    isSave,
    printOnProcess: model.isPrintOnProcess,
    showQuantity: quoteFilter && quoteFilter.quantity || false,
    showUom: quoteFilter && quoteFilter.units || false,
    showPrice: quoteFilter && quoteFilter.price || false,
  };
};

export const jobCoSaveToPreviewDto = (model: JobCoSave): JobChangeOrderDetailsSaveDto => {
  const { quoteFilter } = model;
  return <JobChangeOrderDetailsSaveDto>{
    jobChangeOrder: jobCoSaveToMainDto(model),
    showQuantity: quoteFilter && quoteFilter.quantity || false,
    showUom: quoteFilter && quoteFilter.units || false,
    showPrice: quoteFilter && quoteFilter.price || false,
  };
};

export const jobCoSaveFromDto = (dto: JobChangeOrderDetailsSaveResponseDto): JobCoSave => ({
  ...jobCoDetailsFromMainDto(dto.jobChangeOrder),
  letterTemplate: dto.letterTemplate,
  affectedCostCentersCount: dto.affectedCostCentersCount,
});

export const jobCoElementToDto = (element: JobCoElement): JobChangeOrderDetailsElementDto =>
  <JobChangeOrderDetailsElementDto>omit(element, 'type', 'variationId', 'isExistInJob', 'dirtyType', 'entityState');

export const jobCoPrintRequestFromGeneratedDocumentSet = (docs: JobCoDetailsGeneratedDocumentSet,
                                                          printer?: PrinterFinderListItem,
                                                          authCode?: string): JobCoDetailsPrintRequest => ({
  docs: omit(docs, 'replaceJobOrder'),
  printerId: printer && printer.cloudPrinterId,
  authCode,
});

export const jobCoDocumentSetToEmailAttachments = (docs: JobCoDetailsGeneratedDocumentSet): EmailAttachment[] =>
  <EmailAttachment[]>values(omit(docs, 'jobOrderDocs', 'replaceJobOrder')).filter(Boolean);

export const mergeJobCoDetailsWithQuoteFilter = (details: JobCoSave, quoteFilter: JobCoDetailsQuoteFilter) => ({
  ...details,
  quoteFilter,
});

export const queryJobCoInProducing = ({ produceStatus }: JobCoDetails) => isProduceStatusInProgress(produceStatus);

export const produceRequestForPrintJobOrder = (): JobCoProduceRequest => ({
  printQuoteCopy: false,
  showPrice: false,
  showQuantity: false,
  showUom: false,
  validAffectedCostCenters: false,
  replaceJobOrder: true,
});

// tslint:disable-next-line:max-line-length
export const produceRequestForPrintApprovedWithQuoteCopy = (quoteFilter: JobCoDetailsQuoteFilter): JobCoProduceRequest => ({
  showUom: quoteFilter.units,
  showQuantity: quoteFilter.quantity,
  showPrice: quoteFilter.price,
  replaceJobOrder: false,
  printQuoteCopy: true,
  validAffectedCostCenters: false,
});

export const produceRequestForPrintApprovedWithoutQuoteCopy = (): JobCoProduceRequest => ({
  showUom: false,
  showQuantity: false,
  showPrice: false,
  replaceJobOrder: false,
  printQuoteCopy: false,
  validAffectedCostCenters: false,
});

export const produceRequestForPrintApprovedWithCopy = (quoteFilter: JobCoDetailsQuoteFilter,
                                                       validAffectedCostCenters: boolean): JobCoProduceRequest => ({
  showUom: quoteFilter.units,
  showQuantity: quoteFilter.quantity,
  showPrice: quoteFilter.price,
  replaceJobOrder: false,
  printQuoteCopy: true,
  validAffectedCostCenters,
});

/**
 *  Returns true if CO should be (will be) immediately claimed on approve, otherwise false.
 */
export const isJobCoImmediatelyClaimed = (state: JobCoDetails) => {
  // job change order is immediately claimed
  // either when CO belongs to Lender's Schedule draw rule
  // or when assigned draw rule stage is claimed
  const masterInfo = getFormStateSnapshotValue(state.masterInfo);
  return masterInfo.isLendersSchedule
    || isNotNil(get(find(state.drawRuleStages, { stageNumber: masterInfo.drawRuleStage }), 'accountingDate'));
};

export const isPrintQuoteNeeded = (state: JobCoDetails) => {
  const masterInfo = getFormStateSnapshotValue(state.masterInfo);
  return state.isPrintOnProcess && masterInfo.status === JobVariationStatus.Quote && isNil(masterInfo.producedDate);
};

export const isPrintJobOrderNeeded = (state: JobCoDetails) => {
  const masterInfo = getFormStateSnapshotValue(state.masterInfo);
  return masterInfo.status === JobVariationStatus.Approved && masterInfo.isStatusChanged
    && !isJobCoImmediatelyClaimed(state);
};

export const isPrintApprovedClaimedNeeded = (state: JobCoDetails) => {
  const masterInfo = getFormStateSnapshotValue(state.masterInfo);
  return masterInfo.status === JobVariationStatus.Approved && masterInfo.isStatusChanged
    && isJobCoImmediatelyClaimed(state);
};

export const usePrinterFromDeliveryMethod = (deliveryBy: ContactDeliveryMethod) =>
  deliveryBy === ContactDeliveryMethod.Mail ? JobCoUsePrinter.Mandatory : JobCoUsePrinter.NotRequired;

export const usePrinterForAffectedOrders = ({ deliveryBy, ordersCount, ordersCountPrint }: JobCoDetails) => {
  const isDeliveredByMailForCustomer = deliveryBy === ContactDeliveryMethod.Mail;
  const isSomeOrdersShouldBeDeliveredByMail = ordersCountPrint > 0;
  const isAllOrdersShouldBeDeliveredByMail = ordersCountPrint > 0 && (ordersCount - ordersCountPrint) === 0;

  if (isDeliveredByMailForCustomer && isAllOrdersShouldBeDeliveredByMail) {
    return JobCoUsePrinter.Mandatory;
  } else if (isDeliveredByMailForCustomer || isSomeOrdersShouldBeDeliveredByMail) {
    return JobCoUsePrinter.Optional;
  } else {
    return JobCoUsePrinter.NotRequired;
  }
};

export const isDocumentSetEmpty = (documentSet: JobCoDetailsGeneratedDocumentSet) =>
  isEmpty(omitBy(omit(documentSet, 'replaceJobOrder', 'jobOrderDocs'), isNil))
  && isEmpty(get(documentSet, 'jobOrderDocs'));
